import React, { useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { LuPackageCheck } from "react-icons/lu";
import { FaBarcode } from "react-icons/fa6";
import { TbTruckDelivery } from "react-icons/tb";
import { HiPhotograph } from "react-icons/hi";
import { MdCategory } from "react-icons/md";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { addManualBarcode } from "../../store/actions/user-action";
import Portal from "../Portal";

const OrderDetailsCard = React.memo(({ orderDetails, index, onBarcodeSaved }) => {
  const dispatch = useDispatch();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isBarcodeFormOpen, setIsBarcodeFormOpen] = useState(false);
  const [barcodeValue, setBarcodeValue] = useState("");
  const [isSavingBarcode, setIsSavingBarcode] = useState(false);

  const formattedDate = useMemo(() => {
    if (!orderDetails.createdAt) return '';
    return new Date(orderDetails.createdAt).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, [orderDetails.createdAt]);

  const statusColorClass = useMemo(() => {
    const statusColors = {
      Delivered: "bg-green-100 text-green-800 border-green-200",
      Processing: "bg-blue-100 text-blue-800 border-blue-200",
      Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    };
    return statusColors[orderDetails.status] || "bg-gray-100 text-gray-800 border-gray-200";
  }, [orderDetails.status]);

  const infoItems = useMemo(() => [
    { label: "Tracking", value: orderDetails.tracking_id, icon: <LuPackageCheck className="text-blue-500 w-4 h-4" /> },
    { label: "Barcode", value: orderDetails.bar_code, icon: <FaBarcode className="text-green-500 w-4 h-4" /> },
    { label: "Type", value: orderDetails.category.type, icon: <MdCategory className="text-purple-500 w-4 h-4" /> },
    { label: "Price", value: `₹${orderDetails.category.price}`, icon: <TbTruckDelivery className="text-orange-500 w-4 h-4" /> },
  ], [orderDetails.tracking_id, orderDetails.bar_code, orderDetails.category.type, orderDetails.category.price]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const closeModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const openImageModal = useCallback((image) => {
    setSelectedImage(image);
  }, []);

  const openBarcodeForm = useCallback((event) => {
    event.stopPropagation();
    setBarcodeValue(orderDetails.bar_code || "");
    setIsBarcodeFormOpen(true);
  }, [orderDetails.bar_code]);

  const closeBarcodeForm = useCallback(() => {
    setIsBarcodeFormOpen(false);
    setBarcodeValue("");
  }, []);

  const handleSaveBarcode = useCallback(async () => {
    const trimmedBarcode = barcodeValue.trim();

    if (!trimmedBarcode || isSavingBarcode) {
      return;
    }

    setIsSavingBarcode(true);
    const response = await dispatch(
      addManualBarcode({
        tracking_id: orderDetails.tracking_id,
        barcode: trimmedBarcode,
      })
    );
    setIsSavingBarcode(false);

    if (response?.success) {
      closeBarcodeForm();
      if (onBarcodeSaved) {
        onBarcodeSaved();
      }
    }
  }, [barcodeValue, closeBarcodeForm, dispatch, isSavingBarcode, onBarcodeSaved, orderDetails.tracking_id]);

  const limitedCatalogs = useMemo(() => orderDetails.category.catlogs?.slice(0, 4) || [], [orderDetails.category.catlogs]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
      <div className="p-4 cursor-pointer" onClick={toggleExpanded}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-bold text-sm">
              {index + 1}
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 text-sm">{orderDetails.category.title}</h4>
              <div className="flex items-center space-x-4 text-xs text-gray-500">
                <span>ID: {orderDetails.tracking_id}</span>
                <span>₹{orderDetails.category.price}</span>
                {formattedDate && <span>{formattedDate}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColorClass}`}>
              {orderDetails.status}
            </span>
            {isExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="pt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {infoItems.map((item, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    {item.icon}
                    <span className="text-xs text-gray-500 font-medium">{item.label}</span>
                  </div>
                  <p className="text-xs font-semibold text-gray-800 truncate">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              {!isBarcodeFormOpen && (
                <button
                  type="button"
                  onClick={openBarcodeForm}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-500 to-rose-600 text-white text-xs font-semibold shadow-sm hover:shadow-md hover:from-red-600 hover:to-rose-700 transition-all duration-200"
                >
                  <FaBarcode className="w-3.5 h-3.5" />
                  <span>Add Barcode</span>
                </button>
              )}
            </div>

            {isBarcodeFormOpen && (
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 sm:p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={orderDetails.tracking_id || ""}
                  disabled
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-500"
                />
                <input
                  type="text"
                  value={barcodeValue}
                  onChange={(event) => setBarcodeValue(event.target.value)}
                  placeholder="Enter barcode"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primaryRed/20 focus:border-primaryRed"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSaveBarcode}
                    disabled={isSavingBarcode || !barcodeValue.trim()}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold disabled:opacity-50"
                  >
                    {isSavingBarcode ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={closeBarcodeForm}
                    disabled={isSavingBarcode}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 text-xs font-semibold disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {orderDetails.pics && orderDetails.pics.length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <HiPhotograph className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-medium text-gray-700">Photos ({orderDetails.pics.length})</span>
                  </div>
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    {orderDetails.pics.map((pic, idx) => (
                      <button
                        key={idx}
                        onClick={() => openImageModal(pic)}
                        className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden hover:scale-105 transition-transform duration-200"
                      >
                        <img src={pic} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {orderDetails.category.catlogs && orderDetails.category.catlogs.length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <MdCategory className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-medium text-gray-700">Catalog ({orderDetails.category.catlogs.length})</span>
                  </div>
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    {limitedCatalogs.map((catalog, idx) => (
                      <button
                        key={idx}
                        onClick={() => openImageModal(catalog)}
                        className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden hover:scale-105 transition-transform duration-200"
                      >
                        <img src={catalog} alt={`Catalog ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                    {orderDetails.category.catlogs.length > 4 && (
                      <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-xs text-gray-500">
                        +{orderDetails.category.catlogs.length - 4}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <Portal>
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm p-4"
            onClick={closeModal}
          >
            <div
              className="relative flex h-full w-full items-center justify-center p-4"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                className="absolute right-0 top-0 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-lg hover:bg-gray-100 transition-all duration-200"
                onClick={closeModal}
              >
                <svg className="h-4 w-4 text-gray-800" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={selectedImage}
                alt="Selected"
                className="max-h-[calc(100vh-4rem)] max-w-[calc(100vw-4rem)] object-contain rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
});

OrderDetailsCard.displayName = 'OrderDetailsCard';

export default OrderDetailsCard;
